import React from "react";
import { CONSTRAINT_LABELS, SITING_LABELS } from "../lib/format";
import MethodologyNote from "./MethodologyNote";
import AcceptancePathway from "./AcceptancePathway";

function HBar({ label, count, max, testid }) {
  const pct = max ? Math.round((100 * count) / max) : 0;
  return (
    <div
      className="grid grid-cols-[160px_1fr_40px] items-center gap-3 text-sm"
      data-testid={testid}
    >
      <span className="text-gray-700 truncate">{label}</span>
      <div className="h-2 bg-gray-100 border border-gray-200">
        <div className="h-full bg-[#2F4F4F]" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-gray-600 text-right">
        {count}
      </span>
    </div>
  );
}

/**
 * Stacked "explicit (dark) + implicit (lighter)" bar used for dual-layer preferences.
 */
function DualBar({ label, explicit, withImplicit, total, testid }) {
  const denom = total || 1;
  const explicitPct = Math.round((100 * explicit) / denom);
  const anyPct = Math.round((100 * withImplicit) / denom);
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-900 font-medium">{label}</span>
        <span className="font-mono text-xs text-gray-600">
          <span className="text-[#2F4F4F]">{anyPct}%</span>
          <span className="text-gray-400"> incl. implicit</span>
          <span className="text-gray-300"> · </span>
          <span className="text-gray-700">{explicitPct}%</span>
          <span className="text-gray-400"> explicit</span>
        </span>
      </div>
      <div className="relative h-3 bg-gray-100 border border-gray-200 overflow-hidden">
        {/* any-signal band (lighter) */}
        <div
          className="absolute inset-y-0 left-0 bg-[#2F4F4F]/30"
          style={{ width: `${anyPct}%` }}
        />
        {/* explicit band (darker, on top) */}
        <div
          className="absolute inset-y-0 left-0 bg-[#2F4F4F]"
          style={{ width: `${explicitPct}%` }}
        />
      </div>
      <div className="text-[11px] text-gray-500 font-mono">
        {explicit} explicit · {withImplicit} total (of {total})
      </div>
      <div className="text-[11px] text-gray-500 leading-snug">
        Implicit signal derived from turbine configurations, scale choices and
        siting behaviour.
      </div>
    </div>
  );
}

export default function ThemesTab({ stats, workshops }) {
  if (!stats) return null;

  const n = stats.total_loaded || 1;
  const constraints = stats.top_constraints || [];
  const siting = stats.top_siting_logic || [];
  const conditions = stats.top_conditions || [];

  const maxC = constraints.reduce((m, x) => Math.max(m, x.count), 0) || 1;
  const maxS = siting.reduce((m, x) => Math.max(m, x.count), 0) || 1;
  const maxCond = conditions.reduce((m, x) => Math.max(m, x.count), 0) || 1;

  const counts = stats.counts || {};
  const pct = stats.pct || {};
  const pctE = stats.pct_explicit || {};

  const supportiveInterp = pct.supportive_total ?? 0;
  const supportiveExp = pctE.supportive_total ?? 0;
  const constructive = counts.constrained_by_context_not_opposed ?? 0;
  const sdnpMention = counts.sdnp_mention ?? 0;

  return (
    <div className="space-y-10" data-testid="themes-tab">
      {/* Key insight panel */}
      <section data-testid="key-insight-panel">
        <div className="bg-[#F8FAF9] border-l-4 border-[#2F4F4F] p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Technology acceptance vs. planning constraint
          </h2>
          <p className="text-sm text-gray-700 mt-2 leading-relaxed max-w-3xl">
            Across the {n} workshops, <strong>{supportiveInterp}%</strong>{" "}
            engage supportively with onshore wind once the context is taken
            into account — up from a conservative explicit read of{" "}
            <strong>{supportiveExp}%</strong>. Of the {n}, {constructive} are
            classified as <em>supportive, constrained by context</em>:
            engagement is constructive (they proposed turbines, optimised
            siting, discussed trade-offs) but delivery is limited by SDNP,
            landscape sensitivity or planning assumptions.
          </p>
          <p className="text-sm text-gray-700 mt-3 leading-relaxed max-w-3xl">
            {sdnpMention} workshops explicitly cite SDNP or planning. The
            dataset suggests these are <strong>independent signals</strong>:
            most communities accept the technology yet face real delivery
            constraints in specific locations. A planning constraint is not a
            rejection of wind.
          </p>
          <p className="text-sm text-gray-700 mt-3 leading-relaxed max-w-3xl italic border-l-2 border-[#2F4F4F] pl-3">
            &ldquo;Most workshops engage positively with wind when constraints
            are understood, even where delivery is uncertain.&rdquo;
          </p>
        </div>
      </section>

      <MethodologyNote testid="methodology-note-themes" />

      {/* Acceptance pathway */}
      <AcceptancePathway />

      {/* Dual-layer preferences */}
      <section>
        <SectionHeader
          title="Deployment preferences · explicit vs. implicit"
          subtitle="Dark segment = explicitly stated. Lighter segment = additional workshops where preference is inferred from the configurations, sizes or trade-offs participants chose."
        />
        <div className="bg-white border border-gray-200 p-5 space-y-5">
          <DualBar
            label="Prefer fewer, larger turbines"
            explicit={counts.fewer_larger_explicit ?? 0}
            withImplicit={counts.fewer_larger_any ?? 0}
            total={n}
            testid="dualbar-fewer-larger"
          />
          <DualBar
            label="Prefer clustering"
            explicit={counts.clustering_explicit ?? 0}
            withImplicit={counts.clustering_any ?? 0}
            total={n}
            testid="dualbar-clustering"
          />
        </div>
      </section>

      <section>
        <SectionHeader
          title="Sentiment · two reads of the same dataset"
          subtitle="Interpretive (behaviour-weighted) retains the conservative explicit classification for accountability."
        />
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Interpretive classification" testid="sentiment-interp-card">
            <MiniStat
              label="Strongly supportive"
              value={`${pct.strongly_supportive ?? 0}%`}
            />
            <MiniStat
              label="Supportive, conditional"
              value={`${pct.supportive_conditional ?? 0}%`}
            />
            <MiniStat
              label="Supportive, constrained by context"
              value={`${pct.supportive_but_constrained ?? 0}%`}
            />
            <MiniStat
              label="Constrained / resistant"
              value={`${pct.constrained_resistant ?? 0}%`}
            />
          </Card>
          <Card title="Explicit (conservative) classification" testid="sentiment-explicit-card">
            <MiniStat
              label="Strongly supportive"
              value={`${pctE.strongly_supportive ?? 0}%`}
            />
            <MiniStat
              label="Supportive, conditional"
              value={`${pctE.supportive_conditional ?? 0}%`}
            />
            <MiniStat
              label="Constrained / resistant"
              value={`${pctE.constrained_resistant ?? 0}%`}
            />
          </Card>
        </div>
      </section>

      <section>
        <SectionHeader
          title="Acceptability band"
          subtitle="Workshop-level acceptability of onshore wind, banded high / medium / low."
        />
        <div
          className="bg-white border border-gray-200 p-5"
          data-testid="band-breakdown"
        >
          {Object.entries(stats.acceptability_band_counts || {}).map(
            ([k, v]) => (
              <MiniStat
                key={k}
                label={`${k[0].toUpperCase()}${k.slice(1)} acceptability`}
                value={`${v} of ${n}`}
              />
            )
          )}
        </div>
      </section>

      <section>
        <SectionHeader
          title="Most-cited siting logic"
          subtitle="Where workshops suggest turbines would most appropriately sit."
        />
        <div className="bg-white border border-gray-200 p-5 space-y-2">
          {siting.length === 0 ? (
            <p className="text-sm text-gray-500">
              No siting preferences recorded yet.
            </p>
          ) : (
            siting.map((s) => (
              <HBar
                key={s.key}
                label={SITING_LABELS[s.key] || s.key}
                count={s.count}
                max={maxS}
                testid={`siting-bar-${s.key}`}
              />
            ))
          )}
        </div>
      </section>

      <section>
        <SectionHeader
          title="Most-cited constraints"
          subtitle="The barriers most frequently raised across workshops."
        />
        <div className="bg-white border border-gray-200 p-5 space-y-2">
          {constraints.length === 0 ? (
            <p className="text-sm text-gray-500">No constraints recorded yet.</p>
          ) : (
            constraints.map((c) => (
              <HBar
                key={c.key}
                label={CONSTRAINT_LABELS[c.key] || c.key}
                count={c.count}
                max={maxC}
                testid={`constraint-bar-${c.key}`}
              />
            ))
          )}
        </div>
      </section>

      <section>
        <SectionHeader
          title="Conditions for support"
          subtitle="Recurring enabling conditions extracted across workshops."
        />
        <div className="bg-white border border-gray-200 p-5 space-y-2">
          {conditions.length === 0 ? (
            <p className="text-sm text-gray-500">
              No enabling conditions recorded yet.
            </p>
          ) : (
            conditions.map((c) => (
              <HBar
                key={c.key}
                label={capitalise(c.key)}
                count={c.count}
                max={maxCond}
                testid={`condition-bar-${c.key.replace(/\s+/g, "-")}`}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-3">
      <h2 className="text-xl font-semibold tracking-tight text-gray-900">
        {title}
      </h2>
      {subtitle ? (
        <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
      ) : null}
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider text-[11px]">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-700">{label}</span>
      <span className="font-mono font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function capitalise(s) {
  if (!s) return "";
  return s[0].toUpperCase() + s.slice(1);
}
